import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// ===== Form submission target (FormSubmit) =====
// Submits the enquiry directly to FormSubmit's server-side pipeline.
// The form tag posts native form data, and the JS handler mirrors it via a
// background fetch so the page never navigates away.
const FORM_ENDPOINT = 'https://formsubmit.co/hitesh@settlespace.in';

// ===== 7-day device submission limit (presentational guard only) =====
// Purely a client-side countdown window: max 2 submissions per rolling 7 days
// per device. Never mutates the FormSubmit payload body.
const SUBMIT_COUNT_KEY = 'settlespace_submit_count';
const SUBMIT_TIMESTAMP_KEY = 'settlespace_submit_timestamp';
const SUBMIT_WINDOW_MS = 604800000; // 7 days in milliseconds
const SUBMIT_LIMIT = 2;

interface ContactRow {
  icon: string;
  title: string;
  detail?: string;
  clickable: boolean;
  href?: string;
  copyValue?: string;
}

const contactRows: ContactRow[] = [
  {
    icon: 'ri-whatsapp-line',
    title: 'WhatsApp Us',
    clickable: true,
    href: 'https://wa.me/918591019785',
  },
  {
    icon: 'ri-phone-line',
    title: 'Call Us',
    detail: '+91 85910 19785',
    clickable: true,
    href: 'tel:+918591019785',
  },
  {
    icon: 'ri-mail-line',
    title: 'Email Us',
    detail: 'hitesh@settlespace.in',
    clickable: true,
    href: 'mailto:hitesh@settlespace.in',
    copyValue: 'hitesh@settlespace.in',
  },
  {
    icon: 'ri-map-pin-line',
    title: 'Our Service Area',
    detail: 'Focused on Kharghar, Navi Mumbai and nearby areas.',
    clickable: false,
  },
];

export default function ContactUsSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [mailCopied, setMailCopied] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [detailsError, setDetailsError] = useState(false);
  const successRef = useRef<HTMLDivElement>(null);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleRowClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string, copyValue?: string) => {
    // For mailto links, browsers only open them if a default mail app is
    // registered. As a failsafe, also copy the address so the click always
    // gives feedback, even when no mail handler exists.
    if (copyValue) {
      e.preventDefault();
      const doCopy = () => {
        if (navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(copyValue).catch(() => {});
        }
        setMailCopied(true);
        if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
        copyTimerRef.current = setTimeout(() => setMailCopied(false), 2600);
      };
      doCopy();
      // Attempt to launch the mail client as well (non-blocking).
      window.location.href = href;
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // ===== Sanitized input validation shield (presentational guard only) =====
    // Read-only validation: never mutates the transmitted FormData body.
    const validationEl = e.currentTarget;
    const rawPhone = (new FormData(validationEl).get('phone') as string) || '';
    const digitsOnly = rawPhone.replace(/[^0-9]/g, '');
    let localDigits = digitsOnly;
    if (localDigits.length === 11 && localDigits.startsWith('0')) {
      localDigits = localDigits.slice(1);
    } else if (localDigits.length === 12 && localDigits.startsWith('91')) {
      localDigits = localDigits.slice(2);
    }
    const phoneValid = localDigits.length === 10;
    // Gated phone warning: only flag the phone when the user actually typed
    // something AND it fails the strict 10-digit check. A completely empty
    // field is covered by the global "fill in your details" notice instead.
    const phoneHasInput = rawPhone.trim() !== '';
    const nameValid = (((new FormData(validationEl).get('name') as string) || '').trim()) !== '';
    const typeValid = (((new FormData(validationEl).get('user_type') as string) || '').trim()) !== '';
    const detailsValid = nameValid && phoneValid && typeValid;

    setPhoneError(phoneHasInput && !phoneValid);
    setDetailsError(!detailsValid);

    if (!detailsValid) {
      return;
    }

    // ===== Step 1 & 2: local timestamp + count logic, then limit enforcer =====
    // Read the stored window first, auto-reset if it has aged past 7 days.
    let activeCount = parseInt(window.localStorage.getItem(SUBMIT_COUNT_KEY) || '0', 10);
    if (Number.isNaN(activeCount) || activeCount < 0) activeCount = 0;
    const savedTimestamp = parseInt(window.localStorage.getItem(SUBMIT_TIMESTAMP_KEY) || '0', 10);

    if (savedTimestamp && Date.now() - savedTimestamp > SUBMIT_WINDOW_MS) {
      activeCount = 0;
      window.localStorage.removeItem(SUBMIT_TIMESTAMP_KEY);
      window.localStorage.setItem(SUBMIT_COUNT_KEY, '0');
    }

    if (activeCount >= SUBMIT_LIMIT) {
      setLimitReached(true);
      setFormStatus('idle');
      setFormError('');
      return;
    }

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = formData.get('website_alt') as string;
    if (honeypotValue && honeypotValue.trim() !== '') {
      setFormStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    formData.delete('website_alt');

    setFormStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        body: new FormData(form),
      });

      // On any successful HTTP 200 status, the input fields dissolve and the
      // thank-you card takes over on-page. No body parsing needed.
      if (response.ok || response.status === 200) {
        // ===== Step 4: count & time initialization on successful response =====
        let currentCount = parseInt(window.localStorage.getItem(SUBMIT_COUNT_KEY) || '0', 10);
        if (Number.isNaN(currentCount) || currentCount < 0) currentCount = 0;
        if (currentCount === 0) {
          window.localStorage.setItem(SUBMIT_TIMESTAMP_KEY, String(Date.now()));
        }
        window.localStorage.setItem(SUBMIT_COUNT_KEY, String(currentCount + 1));
        setLimitReached(false);
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        setFormError('Something went wrong. Please try again.');
        setFormStatus('error');
      }
    } catch {
      setFormError('Something went wrong. Please try again.');
      setFormStatus('error');
    }
  };

  return (
    <section className="w-full bg-background-100">
      <div className="w-full px-6 md:px-12 lg:px-16 py-16 md:py-20">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          {/* ===== Left Column ===== */}
          <div className="lg:w-[42%]">
            <p className="text-[0.7rem] tracking-[0.3em] uppercase text-accent-500 mb-4">
              CONTACT US
            </p>

            <h2 className="font-heading text-xl md:text-2xl lg:text-[1.625rem] leading-snug text-foreground-950 mb-10">
              Get in touch with SettleSpace.
            </h2>

            <div className="flex flex-col">
              {contactRows.map((row, index) => {
                const isLast = index === contactRows.length - 1;

                const inner = (
                  <div
                    className={`flex items-center gap-4 py-5 ${
                      !isLast ? 'border-b border-background-200/60' : ''
                    } ${row.clickable ? 'group cursor-pointer' : ''}`}
                  >
                    {/* Icon */}
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-50 border border-background-200/60 flex-shrink-0">
                      <i className={`${row.icon} text-lg text-foreground-700`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground-950 leading-snug">{row.title}</p>
                      {row.detail && (
                        <p className="text-sm text-foreground-600 leading-relaxed mt-0.5 md:whitespace-nowrap">
                          {row.detail}
                        </p>
                      )}
                    </div>

                    {/* Arrow */}
                    {row.clickable && (
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                        <i className="ri-arrow-right-s-line text-lg text-foreground-400 group-hover:text-foreground-700 transition-colors duration-200 group-hover:translate-x-1 transition-transform duration-200" />
                      </div>
                    )}
                  </div>
                );

                if (row.clickable && row.href) {
                  return (
                    <a
                      key={row.title}
                      href={row.href}
                      onClick={(e) => handleRowClick(e, row.href as string, row.copyValue)}
                      className="block transition-all duration-200 hover:-translate-y-1 hover:shadow-md rounded-lg -mx-3 px-3"
                    >
                      {inner}
                    </a>
                  );
                }

                return (
                  <div key={row.title} className="bg-background-200/80 rounded-lg -mx-3 px-3 py-0.5">
                    {inner}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ===== Right Column: Form ===== */}
          <div className="lg:w-[50%]">
            <div className="bg-background-50 border border-background-200/60 rounded-lg p-8 md:p-10">
              {formStatus === 'success' ? (
                <div
                  ref={successRef}
                  className="flex flex-col items-center justify-center py-12 text-center"
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 mb-5">
                    <i className="ri-check-line text-2xl text-accent-600" />
                  </div>
                  <h3 className="font-heading text-lg text-foreground-950 mb-2">
                    Thank you. We'll get in touch with you shortly.
                  </h3>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="mt-6 text-sm text-foreground-600 hover:text-foreground-950 underline underline-offset-4 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Send another enquiry
                  </button>
                </div>
              ) : (
              <form
                name="enquiry-form"
                method="POST"
                action="https://formsubmit.co/hitesh@settlespace.in"
                noValidate
                onSubmit={handleSubmit}
              >
                  <input type="hidden" name="_captcha" value="false" />
                  <input type="hidden" name="_ajax" value="true" />

                  {/* Honeypot */}
                  <div className="honeypot-field">
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                    {/* Name */}
                    <div>
                      <label
                        htmlFor="contact-name"
                        className="block text-[0.8125rem] font-medium text-foreground-800 mb-2"
                      >
                        Name
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        placeholder="Your full name"
                        onChange={() => setDetailsError(false)}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/60 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300/60 transition-colors"
                      />
                    </div>

                    {/* Phone Number */}
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <label
                          htmlFor="contact-phone"
                          className="block text-[0.8125rem] font-medium text-foreground-800"
                        >
                          Phone Number
                        </label>
                        <p
                          className={`flex items-center gap-1 text-xs text-red-600 font-normal transition-opacity duration-200 ${
                            phoneError ? 'opacity-100' : 'opacity-0'
                          }`}
                        >
                          <span className="w-3.5 h-3.5 flex items-center justify-center rounded-full border border-red-600 text-[0.6rem] leading-none font-bold flex-shrink-0">
                            !
                          </span>
                          Incorrect number
                        </p>
                      </div>
                      <input
                        id="contact-phone"
                        name="phone"
                        type="tel"
                        required
                        placeholder="Your phone number"
                        onChange={() => {
                          setPhoneError(false);
                          setDetailsError(false);
                        }}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/60 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300/60 transition-colors"
                      />
                    </div>

                    {/* I am a */}
                    <div>
                      <label
                        htmlFor="contact-user-type"
                        className="block text-[0.8125rem] font-medium text-foreground-800 mb-2"
                      >
                        I am a
                      </label>
                      <div className="relative">
                        <select
                          id="contact-user-type"
                          name="user_type"
                          required
                          onChange={() => setDetailsError(false)}
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-200/60 rounded-md text-sm text-foreground-950 focus:outline-none focus:border-foreground-300/60 transition-colors appearance-none cursor-pointer pr-10"
                          defaultValue=""
                        >
                          <option value="" disabled>
                            Select an option
                          </option>
                          <option value="tenant">Tenant</option>
                          <option value="property_owner">Owner</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <i className="ri-arrow-down-s-line text-base text-foreground-400" />
                        </div>
                      </div>
                    </div>

                    {/* Location / Area */}
                    <div>
                      <label
                        htmlFor="contact-location"
                        className="block text-[0.8125rem] font-medium text-foreground-800 mb-2"
                      >
                        Location / Area
                      </label>
                      <input
                        id="contact-location"
                        name="location"
                        type="text"
                        placeholder="Preferred area or property location"
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200/60 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300/60 transition-colors"
                      />
                    </div>
                  </div>

                  {/* Message / Requirement */}
                  <div className="mt-5">
                    <label
                      htmlFor="contact-message"
                      className="block text-[0.8125rem] font-medium text-foreground-800 mb-2"
                    >
                      Message / Requirement
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder="Tell us what you're looking for..."
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-200/60 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300/60 transition-colors resize-none"
                    />
                    <p className="text-[0.65rem] text-foreground-400 mt-1 text-right">
                      Max 500 characters
                    </p>
                  </div>

                  {/* Error */}
                  {formStatus === 'error' && formError && (
                    <p className="mt-4 text-xs text-red-600 leading-relaxed">
                      {formError}
                    </p>
                  )}

                  {/* Submit */}
                  <div className="mt-5 flex flex-row flex-wrap items-center justify-start gap-3">
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting' || limitReached}
                      className="px-10 py-3 bg-primary-500 text-white text-sm rounded-md hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex-shrink-0"
                    >
                      {formStatus === 'submitting' ? 'Submitting...' : 'Submit'}
                    </button>

                    {/* Step 3: lockout warning notice */}
                    {limitReached && (
                      <p className="text-xs text-amber-600 leading-relaxed">
                        You have already submitted a query
                      </p>
                    )}

                    {/* Catch-all validation warning */}
                    {detailsError && !limitReached && (
                      <p className="flex items-center gap-1 text-xs leading-tight text-red-600 font-normal whitespace-nowrap transition-opacity duration-200">
                        <span className="w-3.5 h-3.5 flex items-center justify-center rounded-full border border-red-600 text-[0.6rem] leading-none font-bold flex-shrink-0">
                          !
                        </span>
                        Please fill in your details
                      </p>
                    )}
                  </div>

                  {/* Privacy note */}
                  <div className="mt-5 sm:mt-3">
                    <p className="flex items-center gap-1.5 text-[0.75rem] md:text-[0.8125rem] text-foreground-600 leading-relaxed">
                      <i className="ri-lock-line text-xs md:text-sm" />
                      <span>Your details are safe with us.</span>
                    </p>
                    <p className="text-[0.65rem] md:text-[0.75rem] text-foreground-500 leading-snug md:leading-relaxed mt-2 sm:mt-1">
                      By submitting this inquiry, you agree to our{' '}
                      <Link
                        to="/?section=privacy"
                        className="underline decoration-1 decoration-foreground-400/50 underline-offset-[3px] hover:decoration-foreground-600/70 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Privacy Policy
                      </Link>
                      .
                    </p>
                  </div>
              </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Copy-to-clipboard confirmation toast */}
      {mailCopied && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-foreground-950 text-background-50 px-4 py-3 rounded-md text-sm shadow-lg">
          <i className="ri-check-line text-base" />
          <span>Email copied: hitesh@settlespace.in</span>
        </div>
      )}

      {/* Scroll success into view on mobile when it appears */}
      {formStatus === 'success' && (
        <MobileSuccessScroll successRef={successRef} />
      )}
    </section>
  );
}

function MobileSuccessScroll({ successRef }: { successRef: React.RefObject<HTMLDivElement | null> }) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isMobile = window.innerWidth < 1024;
    if (!isMobile) return;

    // Small delay to let the DOM paint the success message first
    const timer = setTimeout(() => {
      successRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);

    return () => clearTimeout(timer);
  }, [successRef]);

  return null;
}